import * as THREE from 'three';
import { L as SIM_L, isBoss, type Enemy as SimEnemy, type SimEvent, type Venom, type Vortex } from '@orb/sim';
import { Shape } from '../fx/Particles';
import type { Fx } from '../fx/Fx';
import type { Glows } from '../fx/Glows';
import { Projectiles } from '../fx/Projectiles';
import { VenomPools } from '../fx/VenomPools';
import { GravityWells } from '../fx/GravityWells';
import type { Board } from './Board';
import { KIND_COLOR, PITBOSS, type Kind } from './config';
import type { Enemy, Horde } from './Horde';
import type { Relic } from './Relic';
import { Abilities } from './Abilities';
import type { Driver } from './Driver';
import { fxUniforms } from '../fx/common';
import { gateWound, runeOverload } from '../scene/World';

interface Proj {
  player: number; level: number;
  pos: THREE.Vector3; prev: THREE.Vector3; vel: THREE.Vector3;
  aim: THREE.Vector3; from: THREE.Vector3; t: number; dur: number;
  glow: number; body: number; partner: boolean;
  trailPos: THREE.Vector3; trailTick: number;
}
/** Visual tails of attacks whose damage the sim resolves: the flame channel and the lingering chain lightning. */
interface Channel { kind: 'r' | 'y'; player: number; cell: number; version: number; relic: Relic; target: number; level: number; at: THREE.Vector3; time: number; tick: number; partner: boolean; chain: number[]; splash: boolean }
interface Soul { pos: THREE.Vector3; prev: THREE.Vector3; from: THREE.Vector3; ctrl: THREE.Vector3; ctrl2: THREE.Vector3; to: THREE.Vector3; phase: number; t: number; dur: number; size: number; trail: THREE.Vector3; blood: boolean }
interface BossFx { aura: number; body: number; ring: THREE.Mesh; mat: THREE.ShaderMaterial; shown: number; lag: number; color: number; pit: boolean; auraT: number; entered: boolean }

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _from = new THREE.Vector3(), _hit = new THREE.Vector3(), _c = new THREE.Color();
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const DT = 1 / 60;

/**
 * The fight as seen: the simulation decides every hit, death and reward; this turns its
 * events into projectiles, beams, bursts, souls and boss dressings. Tick-rate visuals advance
 * with the sim; render() interpolates them.
 */
export class Combat {
  readonly abilities: Abilities;
  private projs: Proj[] = [];
  private channels: Channel[] = [];
  private souls: Soul[] = [];
  private readonly bodies = new Projectiles();
  private readonly pools = new VenomPools();
  private readonly wells = new GravityWells(KIND_COLOR.o);
  private bossFx = new Map<number, BossFx>();
  /** Obsidian's marks: a burning sigil over each marked head (a glow), a target ring at the feet now and then. */
  private marks = new Map<number, { glow: number; ring: number }>();
  private readonly venomFx = new WeakMap<Venom, number>();
  private readonly vortexFx = new WeakMap<Vortex, number>();
  private trailClock = 0;
  private soulFrame = 0;
  private overloadT = 0;

  constructor(
    private readonly driver: Driver,
    private readonly board: Board,
    private readonly horde: Horde,
    private readonly fx: Fx,
    private readonly glows: Glows,
    private readonly floorGlows: Glows,
    private readonly scene: THREE.Scene,
    private readonly sealPos: THREE.Vector3,
  ) {
    scene.add(this.bodies.mesh, this.pools.mesh, this.wells.mesh);
    this.abilities = new Abilities(driver, horde, fx, scene);
  }

  private relic(player: number, cell: number) { return this.board.relicAt(player, cell); }
  private partner(player: number) { return player !== this.driver.me; }
  /** Where a player's souls fly home: our seal, or the middle of the ally's altar. */
  private home(player: number, out: THREE.Vector3) {
    return player === this.driver.me ? out.copy(this.sealPos) : out.set(0, 1.0, SIM_L.partnerZ);
  }

  private addBossFx(v: Enemy) {
    const e = v.e, pit = e.cls === 'pitboss', color = pit ? PITBOSS.color : 0xff3a14;
    const aura = this.floorGlows.add(color, v.pos.x, 0.025, v.pos.z, { size: 0.62 * e.scale * 3, opacity: 0.8 * v.reveal, flicker: 0.15 });
    const body = this.glows.add(color, v.pos.x, e.scale, v.pos.z, { size: 2.2 * e.scale, opacity: 0.18 * v.reveal, flicker: 0.1 });
    const mat = new THREE.ShaderMaterial({
      uniforms: { uFill: { value: 1 }, uLag: { value: 1 }, uColor: { value: new THREE.Color(color).multiplyScalar(2.4) }, uFlash: { value: 0 }, uReveal: { value: v.reveal }, uTime: fxUniforms.uTime },
      transparent: true, depthWrite: false,
      vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */ `
        uniform float uFill, uLag, uFlash, uReveal, uTime; uniform vec3 uColor; varying vec2 vUv;
        void main() {
          float x = vUv.x, on = step(x, uFill), lag = step(x, uLag) * (1.0 - on);
          // ten segments, a pale chunk for health just lost, a heartbeat once the boss is nearly dead
          float seg = smoothstep(0.004, 0.0, abs(fract(x * 10.0) - 0.5) - 0.492);
          float low = uFill < 0.25 ? 0.6 + 0.4 * sin(uTime * 10.0) : 1.0;
          vec3 c = mix(vec3(0.08, 0.02, 0.01), uColor * (1.0 + uFlash * 2.0) * low, on);
          c = mix(c, vec3(1.6, 1.4, 1.2), lag);
          c *= 1.0 - seg * 0.75;
          gl_FragColor = vec4(c, (0.35 + 0.65 * max(on, lag)) * uReveal);
        }`,
    });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.62 * e.scale, 0.028, 6, 120), mat);
    ring.rotation.x = -Math.PI / 2; ring.renderOrder = 3;
    ring.position.set(v.pos.x, 0.035, v.pos.z); ring.visible = v.reveal > 0.01;
    this.scene.add(ring);
    this.bossFx.set(v.id, { aura, body, ring, mat, shown: 1, lag: 1, color, pit, auraT: Math.random() * 0.1, entered: v.reveal > 0.6 });
  }

  private clearBoss(id: number) {
    const b = this.bossFx.get(id); if (!b) return;
    this.floorGlows.remove(b.aura); this.glows.remove(b.body);
    this.scene.remove(b.ring); b.ring.geometry.dispose(); b.mat.dispose();
    this.bossFx.delete(id);
  }

  /* ---------------- events ---------------- */

  events(events: readonly SimEvent[]) {
    for (const ev of events) {
      switch (ev.type) {
        case 'prepare': {
          const r = this.relic(ev.player, ev.cell), v = this.horde.get(ev.target);
          if (!r || !v) break;
          r.prepareAttack(this.horde.aim(v, _a), ev.windup);
          this.fx.prepare(ev.kind, r.gemPosition(_from), ev.windup, r.s, ev.level);
          break;
        }
        case 'cancel': this.relic(ev.player, ev.cell)?.cancelAttack(); break;
        case 'shoot': this.shoot(ev); break;
        case 'well': {
          this.wells.add(ev.x, ev.z, ev.radius, ev.dur, ev.level);
          const r = this.relic(ev.player, ev.cell);
          if (r) this.fx.el.gravityCast(r.gemPosition(_from), _a.set(ev.x, 0.1, ev.z), ev.level);
          break;
        }
        case 'time_lock': { const v = this.horde.get(ev.id); if (v) this.fx.el.timeLock(this.horde.aim(v, _a), v.e.scale, ev.duration); break; }
        case 'lob_impact': this.lobImpact(ev.player, ev.level, ev.x, ev.z, ev.radius); break;
        case 'mark': { const v = this.horde.get(ev.id); if (v) this.fx.impact('k', this.horde.aim(v, _a), ev.level, this.partner(ev.player) ? 0.8 : 1); break; }
        // the storm's crack grows with what the eye has gathered (up to twice at 300 kills)
        case 'storm_hit': { const v = this.horde.get(ev.id); if (v) this.fx.impact('s', this.horde.aim(v, _a), ev.level, (this.partner(ev.player) ? 0.8 : 1) * (1 + Math.min(1, ev.charges / 300))); break; }
        case 'arcane_hit': { const v = this.horde.get(ev.id); if (v) this.fx.impact('a', this.horde.aim(v, _a), ev.level, this.partner(ev.player) ? 0.8 : 1); break; }
        case 'eruption': this.fx.iceEruption(_a.set(ev.x, 0.035, ev.z), ev.level); break;
        case 'hit': this.hit(ev.id, ev.kind); break;
        case 'stagger': this.stagger(ev.id); break;
        case 'death': this.die(ev); break;
        case 'leak': this.leak(ev.id, ev.cls === 'brute' || ev.cls === 'herald' || ev.cls === 'pitboss'); break;
        case 'slam': this.slam(ev.id); break;
        case 'landing': { const v = this.horde.get(ev.id); if (v) this.fx.dust(_a.set(v.pos.x, 0.04, v.pos.z), 0.18, ev.high ? 5 : 2, 0.4); break; }
        case 'thaw': { const v = this.horde.get(ev.id); if (v) this.fx.el.thaw(this.horde.aim(v, _a), v.e.scale); break; }
        case 'time_resume': { const v = this.horde.get(ev.id); if (v) this.fx.el.timeResume(this.horde.aim(v, _a), v.e.scale); break; }
      }
    }
  }

  private shoot(ev: Extract<SimEvent, { type: 'shoot' }>) {
    const r = this.relic(ev.player, ev.cell); if (!r) return;
    const partner = this.partner(ev.player), kind = ev.kind, level = ev.level;
    const from = r.gemPosition(_from);
    r.fire();
    const target = this.horde.get(ev.target);
    const aim = target ? this.horde.aim(target, new THREE.Vector3()) : new THREE.Vector3(ev.aimX, ev.aimY, ev.aimZ);
    const dir = _b.subVectors(aim, from).normalize();
    this.fx.launch(kind, from, dir, level, r.s * (partner ? 0.8 : 1));
    const version = this.driver.state.players[ev.player].cells[ev.cell].version;
    if (kind === 'v') { this.fx.el.bloodStrike(from, aim, level); return; }
    if (kind === 'o' || kind === 't') return; // the well and the time locks arrive as their own events
    if (kind === 'b') {
      this.fx.p.emit({ x: ev.aimX, y: ev.aimY, z: ev.aimZ, color: _c.set(0x7ac6ff), intensity: 1.1, life: 0.18, size: 0.9, sizeEnd: 1.2, shape: Shape.Frost, fade: 0.2 });
      return;
    }
    if (kind === 'a') { this.fx.el.arcaneMissile(from, aim, level, ev.dur); return; }
    if (kind === 'k' || kind === 's') { this.fx.el.missile(from, aim, level, ev.dur, KIND_COLOR[kind], kind === 'k' ? 0xffd2a0 : 0xe6feff); return; }
    if (kind === 'y') {
      const chain = ev.chain ?? [ev.target];
      chain.forEach((id) => { const v = this.horde.get(id); if (v) this.fx.impact('y', this.horde.aim(v, _a), level, 0.3 + 0.06 * level); });
      if (level >= 5 && target) this.fx.el.skyStrike(this.horde.aim(target, _a));
      if (this.channels.length < 80) this.channels.push({ kind: 'y', player: ev.player, cell: ev.cell, version, relic: r, target: ev.target, level, at: aim, time: 0, tick: 0, partner, chain, splash: false });
      return;
    }
    if (kind === 'r') {
      if (this.channels.length < 80) this.channels.push({ kind: 'r', player: ev.player, cell: ev.cell, version, relic: r, target: ev.target, level, at: aim, time: 0, tick: 0, partner, chain: [], splash: false });
      return;
    }
    // venom: a lobbed spore
    const lobAim = new THREE.Vector3(ev.aimX, ev.aimY, ev.aimZ);
    const v = dir.clone().multiplyScalar(7); v.y += 3;
    this.projs.push({
      player: ev.player, level, pos: from.clone(), prev: from.clone(), vel: v, aim: lobAim, from: from.clone(), t: 0, dur: ev.dur,
      glow: this.glows.add(KIND_COLOR.g, from.x, from.y, from.z, { size: 0.4 + level * 0.03, opacity: 0.35 }),
      body: this.bodies.add('g', level, from, v, partner ? 0.8 : 1), partner,
      trailPos: from.clone(), trailTick: 0,
    });
  }

  private lobImpact(player: number, level: number, x: number, z: number, radius: number) {
    let best = -1, bd = Infinity;
    this.projs.forEach((p, i) => { if (p.player !== player || p.level !== level) return; const d = Math.hypot(p.aim.x - x, p.aim.z - z); if (d < bd) { bd = d; best = i; } });
    if (best >= 0) { const p = this.projs.splice(best, 1)[0]; this.glows.remove(p.glow); this.bodies.remove(p.body); }
    this.fx.impact('g', _a.set(x, 0.12, z), level, this.partner(player) ? 0.8 : 1);
    this.pools.add(x, z, radius, 3);
  }

  private hit(id: number, kind: Kind) {
    const v = this.horde.get(id); if (!v) return;
    if (v.reveal > 0.5) this.fx.hit(kind, this.horde.aim(v, _hit), isBoss(v.cls) ? 1.6 : 1);
    const b = this.bossFx.get(id); if (b) b.mat.uniforms.uFlash.value = 1;
  }

  private stagger(id: number) {
    const v = this.horde.get(id), b = this.bossFx.get(id); if (!v || !b) return;
    // half and quarter health: armour cracks off and the core flares
    this.fx.el.bossStagger(this.horde.aim(v, _hit), v.e.scale, b.color);
    this.fx.flash(_hit, b.color, 6, 3, 0.25); this.fx.shake(0.1); b.mat.uniforms.uFlash.value = 2;
  }

  private die(ev: Extract<SimEvent, { type: 'death' }>) {
    const v = this.horde.get(ev.id); if (!v) return;
    const e = v.e, kind = ev.kind, boss = isBoss(ev.cls);
    const at = this.horde.aim(v, new THREE.Vector3());
    const killCol = KIND_COLOR[kind];
    const deathCol = ev.cls === 'pitboss' ? PITBOSS.color : 0xff5a20;
    this.fx.pop(at, killCol, boss ? 1.2 : 0.35, 0.16, boss ? 2.2 : 1.3);
    // a kill keeps its element: burned, shattered, electrocuted or melted
    this.fx.el.death(kind, at, e.scale, boss);
    if (boss || Math.random() < 0.35) this.fx.flash(at, boss ? deathCol : killCol, boss ? 10 : 2.2, boss ? 4 : 1.6, boss ? 0.35 : 0.16);
    if (boss) {
      // a staged fall: hit-stop on the killing blow, ruptures, then the column and the shockwave
      this.fx.stop(0.13); this.fx.shake(0.5);
      this.fx.sparks(at, deathCol, 16, 3.5, { up: 0.7, life: 0.5, intensity: 2.2 });
      this.fx.el.bossDeath(at, _b.set(v.pos.x, 0, v.pos.z), e.scale, deathCol, ev.cls === 'pitboss');
      this.fx.beam(_a.set(v.pos.x, 0, v.pos.z), deathCol, 0.9, 4.5, 0.8, 1.6);
      this.clearBoss(ev.id);
    } else this.fx.shake(0.03);
    // the soul leaves the body, then flies home to charge the seal
    const blood = kind === 'v';
    this.fx.el.soulRelease(at, boss ? 2 : 1, blood);
    const home = this.home(ev.owner, new THREE.Vector3());
    ev.souls.forEach((s, k) => {
      const from = at.clone().add(_b.set(rnd(-0.2, 0.2) * e.scale, k * 0.15, rnd(-0.2, 0.2) * e.scale));
      // rise first, then swing round the nearer side of the altar, low and outside its rim
      const side = from.x < home.x ? -1 : 1;
      const ctrl = new THREE.Vector3(from.x + side * rnd(1.4, 2.0), from.y + rnd(1.4, 2), from.z + 0.4);
      const ctrl2 = new THREE.Vector3(home.x + side * rnd(5.2, 5.8), rnd(0.7, 1.1), home.z + (home.z > from.z ? -1 : 1) * rnd(1.4, 2.0));
      this.souls.push({ pos: from.clone(), prev: from.clone(), from, ctrl, ctrl2, to: home, phase: Math.random() * 6.28, t: -s.delay, dur: s.dur, size: boss ? 1.7 : 1, trail: from.clone(), blood });
    });
  }

  private leak(id: number, boss: boolean) {
    gateWound.value.set(Math.min(1, gateWound.value.x + (boss ? 0.35 : 0.1)), fxUniforms.uTime.value);
    _a.set(SIM_L.gateX, 0.7, SIM_L.gateZ);
    this.fx.pop(_a, 0xff3010, 1.2, 0.3, 2);
    this.fx.sparks(_a, 0xff5020, 8, 2);
    // a breach should sting: red ground flash at the arch and a short jolt
    this.fx.floorFlash(_b.set(SIM_L.gateX, 0.03, SIM_L.gateZ), 0xff2a10, boss ? 2.4 : 1.4, 0.4, 2);
    this.fx.flash(_a, 0xff3a14, boss ? 8 : 4, 3, 0.25);
    this.fx.shake(boss ? 0.3 : 0.07);
    this.clearBoss(id);
  }

  private slam(id: number) {
    const v = this.horde.get(id); if (!v) return;
    const e = v.e, b = this.bossFx.get(id), color = b ? b.color : 0xff3a14;
    // the war-cry slam: the weapon strikes the road ahead of the boss
    _a.set(v.pos.x + e.tangentX * 0.55 * e.scale, 0.04, v.pos.z + e.tangentZ * 0.55 * e.scale);
    this.fx.el.bossSlam(_a, e.scale, color);
    this.fx.shake(0.14 * v.reveal);
    if (b) b.mat.uniforms.uFlash.value = 1.5;
  }

  /* ---------------- tick-rate visuals ---------------- */

  /** While Overload runs the altar visibly crackles: gold arcs leap between relics and the rune circle burns gold. */
  private overload(dt: number) {
    const on = this.abilities.hasteT > 0;
    runeOverload.value += ((on ? 1 : 0) - runeOverload.value) * Math.min(1, dt * (on ? 6 : 2.5));
    if (!on || (this.overloadT -= dt) > 0) return;
    this.overloadT = 0.09 + Math.random() * 0.08;
    const relics = this.board.mine.filter((r): r is Relic => !!r && r.state !== 'drag');
    if (relics.length < 2) return;
    const a = relics[(Math.random() * relics.length) | 0];
    let b = a, best = Infinity;
    // jump to a near neighbour, so the arcs crawl across the board instead of spanning it
    for (const r of relics) {
      if (r === a) continue;
      const d = Math.hypot(r.cell.x - a.cell.x, r.cell.z - a.cell.z) + Math.random() * 0.9;
      if (d < best) { best = d; b = r; }
    }
    a.gemPosition(_a); b.gemPosition(_b);
    this.fx.el.arc(_a, _b, 0xffd66d, 0.034, 3, 0.13);
    this.fx.p.emit({ x: _b.x, y: _b.y, z: _b.z, color: _c.set(0xffe08a), intensity: 2, life: 0.14, size: 0.12, sizeEnd: 0.32, shape: Shape.Glow, fade: 1.2 });
    a.pulse(0.25); b.pulse(0.25);
  }

  tick() {
    const dt = DT, st = this.driver.state;
    this.overload(dt);
    // the gate's wound heals slowly between breaches
    gateWound.value.x = Math.max(0, gateWound.value.x - dt * 0.012);
    // the venom works visibly: bubbles seep from the armour now and then
    for (const e of st.enemies) {
      if (!e.alive || e.poisonT <= 0 || Math.random() >= dt * 2.2) continue;
      const v = this.horde.get(e.id); if (v) this.fx.bubbles(this.horde.aim(v, _a), 1, 0.14 * e.scale, 0.55);
    }
    for (const e of st.enemies) {
      const m = this.marks.get(e.id), v = e.alive && e.mark > 0 ? this.horde.get(e.id) : undefined;
      if (!v) { if (m) { this.glows.remove(m.glow); this.marks.delete(e.id); } continue; }
      const mk = m ?? { glow: this.glows.add(KIND_COLOR.k, v.pos.x, 0, v.pos.z, { size: 0.32, opacity: 0 }), ring: 0 };
      if (!m) this.marks.set(e.id, mk);
      const y = 1.85 * e.scale * this.horde.drawScale, fade = Math.min(1, e.mark * 2) * v.reveal;
      this.glows.setPos(mk.glow, v.pos.x, y, v.pos.z);
      this.glows.setOpacity(mk.glow, 0.55 * fade);
      if ((mk.ring -= dt) <= 0) { mk.ring = 0.45; this.fx.ring(_a.set(v.pos.x, 0.04, v.pos.z), KIND_COLOR.k, 0.5 * e.scale, 0.35, 1.4 * fade); }
    }
    for (const [id, m] of this.marks) if (!this.horde.get(id)) { this.glows.remove(m.glow); this.marks.delete(id); }
    for (const w of st.vortices) {
      const t = (this.vortexFx.get(w) ?? 0) - dt;
      if (t <= 0) { this.fx.el.gravitySwirl(_a.set(w.x, 0.1, w.z), w.radius, w.level); this.vortexFx.set(w, 0.07); } else this.vortexFx.set(w, t);
    }
    for (const p of st.venom) {
      const t = (this.venomFx.get(p) ?? 0.2) - dt;
      if (t > 0) { this.venomFx.set(p, t); continue; }
      this.venomFx.set(p, 0.35);
      // the puddle itself is a persistent mesh; particles only add rising fumes and bubbles
      this.fx.bubbles(_a.set(p.x, 0.045, p.z), 2, p.radius * 0.7, 0.55);
      const a = Math.random() * 6.283, r = Math.random() * p.radius * 0.6;
      this.fx.el.smoke(p.x + Math.cos(a) * r, 0.1, p.z + Math.sin(a) * r, 0.08, 0.25, -0.03, 0.25, 0.75, 1.5, 0x5a8a45, 0.18);
    }

    // spores
    for (let i = this.projs.length - 1; i >= 0; i--) {
      const p = this.projs[i];
      p.prev.copy(p.pos);
      _b.copy(p.pos);
      p.t += dt;
      const u = Math.min(1, p.t / p.dur);
      p.pos.lerpVectors(p.from, p.aim, u); p.pos.y += Math.sin(u * Math.PI) * 1.6;
      p.vel.subVectors(p.pos, _b).divideScalar(dt);
      // the sim's impact event removes it; this only guards against a lost one
      if (p.t > p.dur + 0.5) { this.glows.remove(p.glow); this.bodies.remove(p.body); this.projs.splice(i, 1); }
    }

    // flame channels and lingering lightning end with the relic's attack
    for (let i = this.channels.length - 1; i >= 0; i--) {
      const a = this.channels[i], cell = st.players[a.player].cells[a.cell];
      if (cell.version !== a.version || cell.phase !== 'idle' || a.relic.state === 'gone') { this.channels.splice(i, 1); continue; }
      a.time += dt; a.tick -= dt;
      if (a.kind === 'r') {
        const target = this.horde.get(a.target);
        if (target && target.e.alive && !target.dying) this.horde.aim(target, a.at);
        if (a.tick <= 0) {
          // a dense, graded stream; flames lick up at the target on every other tick
          a.tick = 1 / 24; a.splash = !a.splash;
          const scale = (1 + 0.05 * a.level) * (a.partner ? 0.8 : 1);
          this.fx.el.flameStream(a.relic.gemPosition(_a), a.at, a.level, scale);
          if (a.splash) this.fx.el.flameSplash(a.at, a.level, scale);
        }
        if (a.time >= 0.46) this.channels.splice(i, 1);
      } else {
        if (a.tick <= 0) {
          // re-strike the whole chain: higher grades hold the arc longer, so it flickers more times
          a.tick = 0.06; a.relic.gemPosition(_a);
          for (const id of a.chain) { const v = this.horde.get(id); if (!v) continue; this.horde.aim(v, _b); this.fx.el.bolt(_a, _b, a.level); _a.copy(_b); }
        }
        if (a.time >= 0.12 + 0.03 * a.level) this.channels.splice(i, 1);
      }
    }

    // souls to the seal
    for (let i = this.souls.length - 1; i >= 0; i--) {
      const s = this.souls[i];
      s.prev.copy(s.pos);
      s.t += dt;
      if (s.t < 0) continue;
      // cubic Bézier: up out of the body, around the side of the altar, then down into the seal,
      // slow out and accelerating home, with a ghostly sway that dies out on arrival
      const u = Math.min(1, s.t / s.dur), e = u * u * (2.2 - 1.2 * u), w = 1 - e;
      const b0 = w * w * w, b1 = 3 * w * w * e, b2 = 3 * w * e * e, b3 = e * e * e, S = s.to;
      const sway = Math.sin(s.t * 9 + s.phase) * 0.22 * Math.sin(Math.PI * u);
      s.pos.set(
        b0 * s.from.x + b1 * s.ctrl.x + b2 * s.ctrl2.x + b3 * S.x + sway,
        b0 * s.from.y + b1 * s.ctrl.y + b2 * s.ctrl2.y + b3 * S.y + sway * 0.5,
        b0 * s.from.z + b1 * s.ctrl.z + b2 * s.ctrl2.z + b3 * S.z);
      if (u >= 1) {
        this.souls.splice(i, 1);
        this.fx.el.soulArrive(S, s.size, s.blood);
      }
    }
  }

  /** Boss dressings appear with the boss. */
  private adoptBosses() {
    for (const v of this.horde.enemies) if (isBoss(v.cls) && !v.dying && !v.leaked && v.e.alive && !this.bossFx.has(v.id)) this.addBossFx(v);
    for (const id of this.bossFx.keys()) { const v = this.horde.get(id); if (!v || v.dying || v.leaked) this.clearBoss(id); }
  }

  /* ---------------- render ---------------- */

  render(alpha: number, dt: number) {
    this.adoptBosses();
    // Trail density stays bounded on 120/144 Hz displays and during slow motion.
    this.trailClock += dt;
    const trails = this.trailClock >= 1 / 60;
    if (trails) this.trailClock %= 1 / 60;
    for (const p of this.projs) {
      _a.lerpVectors(p.prev, p.pos, alpha);
      this.glows.setPos(p.glow, _a.x, _a.y, _a.z);
      this.bodies.set(p.body, _a, p.vel);
      if (!trails) continue;
      if (_a.distanceToSquared(p.trailPos) < 0.0064) continue;
      p.trailPos.copy(_a); p.trailTick++;
      if (p.trailTick % 2 === 0) {
        // toxic haze behind the spore and a drop that falls away from it
        this.fx.trail(_a, 0x459d4e, 0.1 + p.level * 0.012, 0.7, 0.4);
        this.fx.el.venomDrip(_a, p.vel, p.level);
      }
    }
    this.bodies.flush();
    this.pools.flush();
    this.wells.flush();
    // after a mass kill, tails render on alternate frames so dozens of souls stay within budget
    const crowded = this.souls.length > 12;
    this.soulFrame = (this.soulFrame + 1) & 1;
    for (let n = 0; n < this.souls.length; n++) {
      const s = this.souls[n];
      if (!trails || s.t < 0 || (crowded && (n & 1) !== this.soulFrame)) continue;
      _a.lerpVectors(s.prev, s.pos, alpha);
      // fill the distance covered since the last sample so a fast soul keeps an unbroken tail
      const steps = Math.min(4, Math.max(1, Math.round(Math.ceil(_a.distanceTo(s.trail) / 0.07) * this.fx.density)));
      _b.subVectors(_a, s.trail).multiplyScalar(60);
      for (let k = 1; k <= steps; k++) this.fx.el.soulTrail(_hit.lerpVectors(s.trail, _a, k / steps), _b, s.size, k === steps, s.blood);
      s.trail.copy(_a);
    }
    // Weight: bosses thud and raise dust, runners kick up a light puff.
    for (const v of this.horde.footfalls) {
      const e = v.e;
      if (!e.alive || v.dying) continue;
      const heavy = e.cls === 'pitboss' ? 1 : isBoss(e.cls) ? 0.6 : 0;
      _a.set(v.pos.x + e.tangentX * 0.12 * e.scale, 0.04, v.pos.z + e.tangentZ * 0.12 * e.scale);
      this.fx.dust(_a, heavy ? 0.28 * e.scale : 0.12, heavy ? 4 + heavy * 3 : 2, heavy ? 0.5 : 0.28);
      if (heavy) {
        this.fx.ring(_a, 0x8a6a50, 0.55 * e.scale, 0.35, 0.35 * heavy);
        this.fx.shake(0.012 * heavy * v.reveal);
        // each step leaves a glowing crack that cools over a couple of seconds
        if (v.reveal > 0.6) this.fx.el.bossStep(_a, e.scale * (0.6 + heavy * 0.4), e.cls === 'pitboss' ? PITBOSS.color : 0xff4a18);
      }
    }
    for (const [id, b] of this.bossFx) {
      const v = this.horde.get(id); if (!v) continue;
      const e: SimEnemy = v.e;
      const f = Math.max(0, e.hp / e.maxHp);
      b.shown += (f - b.shown) * Math.min(1, dt * 6);
      // the lost chunk lingers, then drains
      b.lag = b.lag < b.shown ? b.shown : b.lag + (b.shown - b.lag) * Math.min(1, dt * 1.6);
      b.mat.uniforms.uFill.value = b.shown;
      b.mat.uniforms.uLag.value = b.lag;
      if (!b.entered && v.reveal > 0.6) {
        b.entered = true;
        this.fx.el.bossEnter(_a.set(v.pos.x, 0, v.pos.z), e.scale, b.color);
        this.fx.flash(_a.set(v.pos.x, 1, v.pos.z), b.color, 7, 4, 0.4); this.fx.shake(0.14);
      }
      if (e.frozen <= 0 && e.timeStop <= 0 && (b.auraT -= dt) <= 0) { b.auraT = 0.09; this.fx.el.bossAura(_a.set(v.pos.x, 0, v.pos.z), e.scale, b.color, b.pit, v.reveal); }
      b.mat.uniforms.uReveal.value = v.reveal;
      b.mat.uniforms.uFlash.value = Math.max(0, b.mat.uniforms.uFlash.value - dt * 6);
      b.ring.position.set(v.pos.x, 0.035, v.pos.z);
      b.ring.visible = v.reveal > 0.01;
      this.floorGlows.setPos(b.aura, v.pos.x, 0.025, v.pos.z);
      this.glows.setPos(b.body, v.pos.x, 1.0 * e.scale, v.pos.z);
      this.floorGlows.setOpacity(b.aura, 0.8 * v.reveal);
      this.glows.setOpacity(b.body, 0.18 * v.reveal);
    }
  }
}
